/* eslint-disable */
import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  Search,
  Plus,
  Heart,
  Bed,
  Bath,
  Car,
  Maximize,
  SlidersHorizontal,
  Home,
  Columns2,
} from "lucide-react";
import { propertiesApi, photoUrl } from "./api";
import { formatInr } from "../utils/inrFormat";
import "./PropertiesBrowse.css";

const FILTERS = [
  { id: "all", label: "All" },
  { id: "rent", label: "For Rent" },
  { id: "sale", label: "For Sale" },
];

export default function PropertiesBrowse() {
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState("all");
  const [q, setQ] = useState("");
  const [debouncedQ, setDebouncedQ] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [showFilters, setShowFilters] = useState(false);
  const [bedrooms, setBedrooms] = useState("");
  const [furnishing, setFurnishing] = useState("");
  const [compareIds, setCompareIds] = useState(new Set());
  const navigate = useNavigate();

  const toggleCompare = (id) => {
    setCompareIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else if (next.size < 4) {
        next.add(id);
      }
      return next;
    });
  };
  // PropertiesBrowse is mounted in both the public and the authenticated route
  // trees. Member-only actions (listing a villa, saved/my listings) must stay
  // hidden from logged-out visitors — there are no public routes for them, and
  // they require a session. The public tree has no AuthProvider, so read the
  // session straight from localStorage (the same key AuthContext uses).
  const isLoggedIn = !!localStorage.getItem("access_token");

  useEffect(() => {
    const t = setTimeout(() => setDebouncedQ(q.trim()), 250);
    return () => clearTimeout(t);
  }, [q]);

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const r = await propertiesApi.list({
        type: filter === "all" ? undefined : filter,
        q: debouncedQ || undefined,
        bedrooms: bedrooms ? Number(bedrooms) : undefined,
        furnishing: furnishing || undefined,
      });
      setItems(r.items || []);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [filter, debouncedQ, bedrooms, furnishing]);

  useEffect(() => {
    load();
  }, [load]);

  return (
    <div className="vsp-br-shell">
      <div className="vsp-br-head">
        <div>
          <h1>Properties</h1>
          <p className="vsp-br-sub">
            Villas for rent and sale across Vishal Sanjivini.
          </p>
        </div>
        {isLoggedIn && (
          <div className="vsp-br-head-actions">
            <button
              className="vsp-br-add-secondary"
              onClick={() => navigate("/properties/saved")}
            >
              <Heart size={14} /> Saved
            </button>
            <button
              className="vsp-br-add-secondary"
              onClick={() => navigate("/properties/me")}
            >
              My listings
            </button>
            <button
              className="vsp-br-add"
              onClick={() => navigate("/properties/new")}
            >
              <Plus size={14} /> List your villa
            </button>
          </div>
        )}
      </div>

      <div className="vsp-br-controls">
        <div className="vsp-br-search">
          <Search size={16} />
          <input
            type="search"
            placeholder="Search by villa number, description…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
          />
        </div>
        <div className="vsp-br-pills">
          {FILTERS.map((f) => (
            <button
              key={f.id}
              className={`vsp-br-pill ${filter === f.id ? "active" : ""}`}
              onClick={() => setFilter(f.id)}
            >
              {f.label}
            </button>
          ))}
        </div>
        <button
          className="vsp-br-filter-btn"
          onClick={() => setShowFilters((v) => !v)}
        >
          <SlidersHorizontal size={14} /> Filters
        </button>
      </div>

      {showFilters && (
        <div className="vsp-br-filters">
          <div className="vsp-br-filter">
            <label>Bedrooms</label>
            <div className="vsp-br-bed-pills">
              {[1, 2, 3, 4, 5].map((n) => (
                <button
                  key={n}
                  className={`vsp-br-pill ${
                    Number(bedrooms) === n ? "active" : ""
                  }`}
                  onClick={() =>
                    setBedrooms((b) =>
                      Number(b) === n ? "" : String(n)
                    )
                  }
                >
                  {n === 5 ? "5+" : `${n}+`}
                </button>
              ))}
            </div>
          </div>
          <div className="vsp-br-filter">
            <label>Furnishing</label>
            <div className="vsp-br-bed-pills">
              {[
                ["FURNISHED", "Furnished"],
                ["SEMI", "Semi"],
                ["UNFURNISHED", "Unfurnished"],
              ].map(([v, label]) => (
                <button
                  key={v}
                  className={`vsp-br-pill ${
                    furnishing === v ? "active" : ""
                  }`}
                  onClick={() => setFurnishing((f) => (f === v ? "" : v))}
                >
                  {label}
                </button>
              ))}
            </div>
          </div>
          <div className="vsp-br-filter">
            <button
              className="vsp-br-reset"
              onClick={() => {
                setBedrooms("");
                setFurnishing("");
              }}
            >
              Reset
            </button>
          </div>
        </div>
      )}

      {error && <div className="vsp-br-error">{error}</div>}
      {loading && <div className="vsp-br-empty">Loading…</div>}

      {!loading && items.length === 0 && (
        <div className="vsp-br-empty-card">
          <Home size={28} strokeWidth={1.4} />
          <h3>No listings yet</h3>
          <p>
            Be the first to list your villa — it's free and goes live
            instantly.
          </p>
        </div>
      )}

      {items.length > 0 && (
        <div className="vsp-br-grid">
          {items.map((p) => (
            <Card
              key={p.id}
              property={p}
              onSavedChange={load}
              isLoggedIn={isLoggedIn}
              compareSelected={compareIds.has(p.id)}
              onToggleCompare={() => toggleCompare(p.id)}
              canAddToCompare={compareIds.size < 4 || compareIds.has(p.id)}
            />
          ))}
        </div>
      )}

      {compareIds.size >= 2 && (
        <div className="vsp-br-compare-bar">
          <div className="vsp-br-compare-bar-inner">
            <span className="vsp-br-compare-count">
              <Columns2 size={16} /> {compareIds.size} selected
            </span>
            <button
              className="vsp-br-compare-clear"
              onClick={() => setCompareIds(new Set())}
            >
              Clear
            </button>
            <button
              className="vsp-br-compare-go"
              onClick={() => navigate(`/properties/compare?ids=${[...compareIds].join(",")}`)}
            >
              Compare →
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function Card({ property, onSavedChange, isLoggedIn, compareSelected, onToggleCompare, canAddToCompare }) {
  const [saved, setSaved] = useState(property.is_saved);
  const [busy, setBusy] = useState(false);

  const onHeart = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (busy) return;
    setBusy(true);
    try {
      if (saved) {
        await propertiesApi.unsave(property.id);
        setSaved(false);
      } else {
        await propertiesApi.save(property.id);
        setSaved(true);
      }
      onSavedChange && onSavedChange();
    } catch (_) {
    } finally {
      setBusy(false);
    }
  };

  const price =
    property.type === "RENT"
      ? formatInr(property.monthly_rent_inr, { monthly: true })
      : formatInr(property.expected_price_inr);

  const villaNum = property.villa?.villa_number || "—";
  const bhk = `${property.bedrooms} BHK`;
  const furnLabel =
    property.furnishing === "FURNISHED"
      ? "Furnished"
      : property.furnishing === "SEMI"
      ? "Semi-Furnished"
      : "Unfurnished";

  return (
    <Link to={`/properties/${property.id}`} className="vsp-br-card">
      <div className="vsp-br-card-img">
        {property.primary_photo_url ? (
          <img src={photoUrl(property.primary_photo_url)} alt={villaNum} />
        ) : (
          <div className="vsp-br-card-placeholder">
            <Home size={32} strokeWidth={1.2} />
          </div>
        )}
        <span
          className={`vsp-br-card-type ${
            property.type === "RENT" ? "rent" : "sale"
          }`}
        >
          {property.type === "RENT" ? "For Rent" : "For Sale"}
        </span>
        {isLoggedIn && (
          <button
            type="button"
            className={`vsp-br-card-heart ${saved ? "active" : ""}`}
            onClick={onHeart}
            disabled={busy}
            aria-label={saved ? "Unsave" : "Save"}
          >
            <Heart size={16} fill={saved ? "#dc2626" : "none"} />
          </button>
        )}
        <button
          type="button"
          className={`vsp-br-card-compare ${compareSelected ? "selected" : ""}`}
          onClick={(e) => { e.preventDefault(); e.stopPropagation(); onToggleCompare?.(); }}
          disabled={!canAddToCompare}
          aria-label={compareSelected ? "Remove from compare" : "Add to compare"}
          title={compareSelected ? "Remove from compare" : canAddToCompare ? "Add to compare (max 4)" : "Compare list full"}
        >
          <Columns2 size={13} />
        </button>
      </div>

      <div className="vsp-br-card-body">
        <h3>{villaNum}</h3>
        <div className="vsp-br-card-bhk">
          {bhk} · Independent Villa
        </div>
        <div className="vsp-br-card-price">{price}</div>
        {property.type === "RENT" && property.monthly_maintenance_inr > 0 && (
          <div className="vsp-br-card-extras">
            + {formatInr(property.monthly_maintenance_inr, { monthly: true })}{" "}
            maintenance
          </div>
        )}
        <div className="vsp-br-card-icons">
          <span title="Bedrooms">
            <Bed size={13} /> {property.bedrooms}
          </span>
          <span title="Bathrooms">
            <Bath size={13} /> {property.bathrooms}
          </span>
          <span title="Parking">
            <Car size={13} /> {property.parking}
          </span>
          <span title="Built-up area">
            <Maximize size={13} /> {property.built_up_area_sqft.toLocaleString("en-IN")} sqft
          </span>
        </div>
        <div className="vsp-br-card-foot">
          <span className="vsp-br-furn">{furnLabel}</span>
          {property.published_at && (
            <span className="vsp-br-when">
              {timeAgo(property.published_at)}
            </span>
          )}
        </div>
      </div>
    </Link>
  );
}

function timeAgo(iso) {
  if (!iso) return "";
  const then = new Date(iso).getTime();
  const diff = Math.max(1, Math.floor((Date.now() - then) / 1000));
  if (diff < 60) return "Just listed";
  const m = Math.floor(diff / 60);
  if (m < 60) return `${m} min ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h} hr ago`;
  const d = Math.floor(h / 24);
  if (d < 7) return `Listed ${d} day${d > 1 ? "s" : ""} ago`;
  return new Date(iso).toLocaleDateString();
}
